/**
 * <brand-header> — manages the desktop mega menu / dropdown disclosures of
 * sections/brand-header.liquid.
 *
 * Mega menu panels (blocks/header-megamenu.liquid) render server-side via `content_for 'blocks'`
 * but can't be matched to a nav link server-side (see that file's doc comment for the measured
 * cause: section.blocks can't be read back reliably for a block that isn't itself content_for'd).
 * So on init, #upgradeMegamenuItems matches each panel to its nav <li> by comparing the link's
 * handle (`data-menu-handle`, identical in every language) against the panel's
 * `data-menu-handles` (comma-separated) and wires up
 * a disclosure trigger for it — reusing the existing trigger if the link already has one (native
 * children dropdown) or building one from the <template> in the section.
 *
 * One panel open at a time. Opens on click/tap (keyboard-activatable via the native <button>)
 * and, on pointer devices that support hover, on pointerenter with a short close delay so the
 * pointer can travel from the trigger down into the panel (or vice versa, since a mega menu
 * panel is not a DOM descendant of its trigger's <li>). Escape closes the open panel and returns
 * focus to its trigger. Clicking or focusing outside both the trigger's item and the panel closes
 * it silently.
 *
 * The mobile menu (MENU-MOBILE-TIROIR-VS-ACCORDEON) is a plain inline disclosure, never Horizon's
 * header-drawer: #upgradeMobileMenuToggle wires blocks/_header-mobile-menu.liquid's trigger button
 * to the panel rendered directly in sections/brand-header.liquid, toggling its `hidden` attribute
 * and `aria-expanded` — no focus trap, no Escape-to-close beyond the one added here, no backdrop,
 * matching the mockup's own `menuOpen` state exactly. Clicking any link inside the panel closes it
 * (mockup's `onClick={() => setMenuOpen(false)}` on every link), but a `<summary>` click (the
 * Accessoires/Prestations accordions) does not, since those toggle their own native `<details>`
 * independently, exactly like the mockup's separate `mobileAcc`/`mobilePresta` state.
 *
 * #upgradeMobileAccordions: `header-mobile-accordion` blocks render alongside the mega menu (same
 * `content_for 'blocks'` call — see that block's doc comment for why they can't render directly
 * inside the panel body), so on init each `[data-mobile-accordion]` node is moved into the panel's
 * `[data-mobile-accordion-slot]` placeholder. The content is real, server-rendered HTML from the
 * very first response (crawlable); only its final DOM position is client-side, and
 * sections/brand-header.liquid's stylesheet keeps it hidden at its render location the whole time
 * so there is no flash regardless of when this runs.
 */
class BrandHeader extends HTMLElement {
  #openEntry = null;
  #closeTimer = null;
  #hoverCapable = window.matchMedia('(hover: hover)').matches;

  connectedCallback() {
    this.#init();
    document.addEventListener('shopify:section:load', this.#onSectionLoad);
  }

  disconnectedCallback() {
    document.removeEventListener('shopify:section:load', this.#onSectionLoad);
    document.removeEventListener('click', this.#onDocumentClick, true);
    document.removeEventListener('keydown', this.#onKeydown, true);
    document.removeEventListener('focusin', this.#onFocusIn, true);
  }

  #onSectionLoad = (event) => {
    if (event.target?.contains?.(this) || event.target === this) this.#init();
  };

  #init() {
    this.#upgradeMegamenuItems();
    this.#upgradeMobileAccordions();
    this.#upgradeMobileMenuToggle();

    this.entries = Array.from(this.querySelectorAll('[data-brand-header-item]'))
      .map((item) => {
        const trigger = item.querySelector('[data-brand-header-trigger]');
        const panelId = trigger?.getAttribute('aria-controls');
        const panel = panelId && document.getElementById(panelId);
        return trigger && panel ? { item, trigger, panel } : null;
      })
      .filter(Boolean);

    for (const entry of this.entries) {
      if (entry.trigger.dataset.brandHeaderBound) continue;
      entry.trigger.dataset.brandHeaderBound = 'true';

      entry.trigger.addEventListener('click', () => {
        // On a hover-capable device, a real click also fires pointerenter first (the mouse moves
        // there before it clicks), which already opens the panel via hover below — so a plain
        // toggle would immediately close what the click's own hover just opened. Closing stays
        // reachable there via Escape, hover-leave or an outside click; click only ever OPENS.
        // On a touch device (no hover), click is the only signal, so it toggles as expected.
        if (entry === this.#openEntry) {
          if (!this.#hoverCapable) this.#close(entry, { focusTrigger: false });
        } else {
          this.#open(entry);
        }
      });

      if (this.#hoverCapable) {
        entry.item.addEventListener('pointerenter', () => this.#open(entry));
        entry.item.addEventListener('pointerleave', () => this.#scheduleClose(entry));
        entry.panel.addEventListener('pointerenter', () => this.#cancelClose());
        entry.panel.addEventListener('pointerleave', () => this.#scheduleClose(entry));
      }
    }

    document.addEventListener('click', this.#onDocumentClick, true);
    document.addEventListener('keydown', this.#onKeydown, true);
    document.addEventListener('focusin', this.#onFocusIn, true);
  }

  /** Matches each mega menu panel to its nav <li> by link handle and wires up a trigger. Idempotent. */
  #upgradeMegamenuItems() {
    const panels = this.querySelectorAll('[data-brand-header-megamenu-panel]');
    if (!panels.length) return;

    const labelPattern = this.dataset.submenuLabelPattern || '';
    const triggerTemplate = this.querySelector('[data-brand-header-trigger-template]');

    for (const panel of panels) {
      const handles = (panel.dataset.menuHandles || '').split(',').filter(Boolean);
      if (!handles.length) continue;

      const item = Array.from(this.querySelectorAll('[data-brand-header-item]:not([data-brand-header-upgraded])')).find(
        (candidate) => handles.includes(candidate.dataset.menuHandle),
      );
      if (!item) continue;

      item.dataset.brandHeaderUpgraded = 'true';
      item.classList.remove('brand-header__nav-item--dropdown');
      item.classList.add('brand-header__nav-item--megamenu');

      let trigger = item.querySelector('[data-brand-header-trigger]');
      if (trigger) {
        trigger.setAttribute('aria-controls', panel.id);
        continue;
      }

      const link = item.querySelector('.brand-header__link, .brand-header__highlight');
      if (!link || !triggerTemplate) continue;

      const wrapper = document.createElement('span');
      wrapper.className = 'brand-header__trigger';
      link.replaceWith(wrapper);
      wrapper.append(link);

      trigger = triggerTemplate.content.firstElementChild.cloneNode(true);
      trigger.setAttribute('aria-controls', panel.id);
      trigger.setAttribute('aria-label', labelPattern.replace('XTITLEX', link.textContent.trim()));
      wrapper.append(trigger);
    }
  }

  #upgradeMobileAccordions() {
    const slot = this.querySelector('[data-mobile-accordion-slot]');
    if (!slot) return;

    const accordions = this.querySelectorAll('[data-mobile-accordion]');
    if (accordions.length) slot.before(...accordions);
    slot.remove();
  }

  /** Wires the hamburger trigger to the inline mobile menu panel. Idempotent. */
  #upgradeMobileMenuToggle() {
    const trigger = this.querySelector('[data-brand-mobile-menu-trigger]');
    if (!trigger || trigger.dataset.brandMobileMenuBound) return;

    const panel = document.getElementById(trigger.getAttribute('aria-controls'));
    if (!panel) return;
    trigger.dataset.brandMobileMenuBound = 'true';

    const setOpen = (open) => {
      trigger.setAttribute('aria-expanded', String(open));
      panel.hidden = !open;
      // The bar above the header scrolls away, so the panel's offset is only known once it is shown.
      if (open) panel.style.setProperty('--brand-mobile-panel-top', `${Math.round(panel.getBoundingClientRect().top)}px`);
    };

    trigger.addEventListener('click', () => setOpen(panel.hidden));

    // Mockup closes the panel on any link click inside it (products, collections, prestations,
    // secondary menu), but not on a <summary> click — that only toggles its own <details>.
    panel.addEventListener('click', (event) => {
      if (event.target.closest('a')) setOpen(false);
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && !panel.hidden) {
        setOpen(false);
        trigger.focus();
      }
    });
  }

  #open(entry) {
    this.#cancelClose();
    if (this.#openEntry && this.#openEntry !== entry) this.#close(this.#openEntry, { focusTrigger: false });

    entry.item.classList.add('is-open');
    entry.trigger.setAttribute('aria-expanded', 'true');
    entry.panel.classList.add('is-open');
    entry.panel.removeAttribute('inert');
    this.#openEntry = entry;
  }

  #close(entry, { focusTrigger }) {
    entry.item.classList.remove('is-open');
    entry.trigger.setAttribute('aria-expanded', 'false');
    entry.panel.classList.remove('is-open');
    entry.panel.setAttribute('inert', '');
    if (this.#openEntry === entry) this.#openEntry = null;
    if (focusTrigger) entry.trigger.focus();
  }

  #scheduleClose(entry) {
    this.#cancelClose();
    this.#closeTimer = window.setTimeout(() => {
      if (this.#openEntry === entry) this.#close(entry, { focusTrigger: false });
    }, 300);
  }

  #cancelClose() {
    if (this.#closeTimer) {
      window.clearTimeout(this.#closeTimer);
      this.#closeTimer = null;
    }
  }

  #onDocumentClick = (event) => {
    if (!this.#openEntry) return;
    if (this.#openEntry.item.contains(event.target) || this.#openEntry.panel.contains(event.target)) return;
    this.#close(this.#openEntry, { focusTrigger: false });
  };

  #onFocusIn = (event) => {
    if (!this.#openEntry) return;
    if (this.#openEntry.item.contains(event.target) || this.#openEntry.panel.contains(event.target)) return;
    this.#close(this.#openEntry, { focusTrigger: false });
  };

  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.#openEntry) return;
    event.stopPropagation();
    this.#close(this.#openEntry, { focusTrigger: true });
  };
}

if (!customElements.get('brand-header')) {
  customElements.define('brand-header', BrandHeader);
}
